import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HighlightText from '@hkm/components/shared/Utilities/HightlightText/HighlightText';
import APP_CONSTANTS from '@hkm/constants/app.constants';
import { debounce } from 'lodash';

import {
  AcButton,
  AcButtonPattern
} from '@ac/mobile-components/dist/components/button';
import { AcDivider } from '@ac/mobile-components/dist/components/divider';
import { AcFieldText } from '@ac/mobile-components/dist/components/field';
import { AcFlex } from '@ac/mobile-components/dist/components/flex';
import { AcIcon } from '@ac/mobile-components/dist/components/icon';
import { AcRadioButton } from '@ac/mobile-components/dist/components/radio-button';
import { AcText } from '@ac/mobile-components/dist/components/text';
import {
  AlignItems,
  FlexDirection,
  Icon,
  TextSize
} from '@ac/mobile-components/dist/enums';

import './SearchField.css';

interface Option {
  id: string;
  text: string;
}

interface ComponentProps {
  optionsArray: Option[];
  label: string;
  onSearch: (value?: string) => void;
  onOptionSelect: (value?: string) => void;
  onClearSelection: () => void;
  className?: string;
}

const SearchField = (props: ComponentProps) => {
  const { t } = useTranslation();
  const {
    label,
    className,
    onClearSelection,
    onSearch,
    optionsArray,
    onOptionSelect
  } = props;
  const [selectedOption, setSelectedOption] = useState<Option | undefined>();
  const [searchValue, setSearchValue] = useState<string | undefined>();

  const callSearch = useCallback(
    debounce((value?: string) => {
      onSearch(value);
    }, APP_CONSTANTS.DEBOUNCE_TIMEOUT),
    []
  );

  const onSearchChange = useCallback(
    (value?: string) => {
      setSearchValue(value);
      value?.length && value?.length > 1 && callSearch(value);
      if (!value) {
        onOptionSelect(undefined);
        setSelectedOption(undefined);
        callSearch(undefined);
      }
    },
    [callSearch, onOptionSelect]
  );

  const clearSelectedValue = useCallback(() => {
    onClearSelection();
    setSearchValue(undefined);
    setSelectedOption(undefined);
    onOptionSelect(undefined);
  }, [onClearSelection, onOptionSelect]);

  const searchInfoText = useMemo(() => {
    if (!searchValue || searchValue.length < 2) {
      return t('COMPONENTS.SEARCH_FIELD.ENTER_CHARACTERS');
    }
    if (searchValue && !optionsArray.length) {
      return t('GLOBAL.NO_RESULTS_FOUND');
    }
    return '';
  }, [optionsArray.length, searchValue, t]);

  return (
    <AcFlex
      className={`full-width search-field position-relative ${className || ''}`}
      direction={FlexDirection.column}
      grow
    >
      <AcFieldText
        value={searchValue}
        className={selectedOption ? 'selected-option' : ''}
        label={label}
        icon={Icon.Search}
        onChange={onSearchChange}
      />
      {searchInfoText && (
        <AcText size={TextSize.Main3}>{searchInfoText}</AcText>
      )}
      {selectedOption && (
        <AcButton
          className="clear-button"
          pattern={AcButtonPattern.Tertiary}
          onClick={clearSelectedValue}
        >
          <AcIcon icon={Icon.Cancel} />
        </AcButton>
      )}

      {searchValue &&
        searchValue.length > 1 &&
        optionsArray.map(option => {
          const setSelectedValue = () => {
            setSelectedOption(option);
            onOptionSelect(option.id);
          };

          return (
            <AcFlex
              key={option.id}
              className="full-width"
              direction={FlexDirection.column}
              onClick={setSelectedValue}
            >
              <AcFlex alignItems={AlignItems.center}>
                <AcDivider />
                <AcRadioButton checked={option.id === selectedOption?.id} />
                <HighlightText text={option.text} searchValue={searchValue} />
              </AcFlex>
            </AcFlex>
          );
        })}
    </AcFlex>
  );
};

export default SearchField;



// WEBPACK FOOTER //
// ./src/components/shared/Fields/SearchField/SearchField.tsx